import client from '../api/client';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function isPushNotificationSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getNotificationPermissionStatus() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) return 'denied';
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

// Fetch the VAPID public key from the backend (so it always matches the server's private key)
async function getVapidPublicKey() {
  try {
    const res = await client.get('/notifications/push/vapid-public-key');
    return res.data?.vapidPublicKey || null;
  } catch (err) {
    console.error('[push] Failed to fetch VAPID public key from server:', err.message);
    // Fall back to env var if server is unreachable
    return process.env.REACT_APP_VAPID_PUBLIC_KEY || null;
  }
}

export async function subscribeToPushNotifications() {
  if (!isPushNotificationSupported()) {
    console.log('[push] Not supported in this browser');
    return null;
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    console.log('[push] Permission not granted:', permission);
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Always re-subscribe with the current server key (handles key rotation)
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      console.error('[push] No VAPID public key available');
      return null;
    }

    // Check if existing subscription uses the same key
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      // Unsubscribe if key might have changed (key rotation or .env fix)
      const existingKey = btoa(String.fromCharCode(...new Uint8Array(existing.options.applicationServerKey)));
      const normalizedExisting = existingKey.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      const normalizedNew = vapidPublicKey.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      if (normalizedExisting !== normalizedNew) {
        console.log('[push] VAPID key changed, re-subscribing');
        await existing.unsubscribe();
      } else {
        // Same key — just re-send to backend to ensure it's stored
        await client.post('/notifications/push/subscribe', { subscription: existing.toJSON() });
        console.log('[push] Re-confirmed existing subscription with backend');
        return existing;
      }
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    await client.post('/notifications/push/subscribe', { subscription: subscription.toJSON() });
    console.log('[push] New subscription saved to backend');
    return subscription;
  } catch (err) {
    console.error('[push] Subscription error:', err.message);
    return null;
  }
}

export async function subscribeStudentOnLogin(user) {
  if (!user) return null;
  if (!['student', 'teacher', 'admin'].includes(user.role)) return null;
  try {
    return await subscribeToPushNotifications();
  } catch (err) {
    console.error('[push] Auto-subscribe on login failed:', err.message);
    return null;
  }
}

export async function unsubscribeFromPushNotifications() {
  if (!isPushNotificationSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe();
  } catch (err) {
    console.error('[push] Unsubscribe error:', err.message);
  }
}

// Force a completely fresh subscription — unsubscribes current, creates new, saves to backend.
// Use when push is confirmed "sent" by server but nothing arrives on device.
export async function forceResubscribe() {
  if (!isPushNotificationSupported()) return null;
  if (Notification.permission !== 'granted') return null;
  try {
    const registration = await navigator.serviceWorker.ready;

    // Nuke any existing browser-side subscription
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
      console.log('[push] Force-unsubscribed old subscription:', existing.endpoint.slice(0, 50));
    }

    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      console.error('[push] No VAPID public key for force resubscribe');
      return null;
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    await client.post('/notifications/push/subscribe', { subscription: subscription.toJSON() });
    console.log('[push] Force resubscribed, new endpoint:', subscription.endpoint.slice(0, 50));
    return subscription;
  } catch (err) {
    console.error('[push] Force resubscribe error:', err.message);
    return null;
  }
}

export async function getCurrentPushSubscription() {
  if (!isPushNotificationSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}
